import { InfoCircleOutlined } from '@ant-design/icons';
import { Checkbox, Form, Input } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import type { NamePath } from 'rc-field-form/es/interface';
import { useCallback, useMemo } from 'react';

import { Mode } from 'app/components/commons/Hotel/Types';
import { Option, Select } from 'app/components/fields/Select';
import { useHotels } from 'app/hooks/data/hotels/useHotel';
import { ChannelManager } from 'app/typings/channelManagers';

import './BasicInfo.scss';

const RULES = [{ required: true, message: 'Required' }];

type BasicInfoValues = {
  hotelId?: number;
  slug?: string;
  aliasForHotel?: string;
  aliasForUser?: string;
  dayPackage?: boolean;
  channelManagerRateCode?: string | null;
  [key: string]: unknown;
};

type Props = {
  mode?: string;
  values?: BasicInfoValues;
  newHotel: boolean;
  onHotelSelect: (hotelId: number) => void;
  setFieldValue: (name: NamePath, value: unknown) => void;
};

export const BasicInfo = ({
  mode = 'edit',
  values,
  newHotel,
  onHotelSelect,
  setFieldValue,
}: Props) => {
  const { data: hotels } = useHotels();

  const selectedHotel = useMemo(
    () => hotels?.find((hotel) => hotel.id === values?.hotelId),
    [hotels, values?.hotelId]
  );

  const channelManagerRateCodeRules = useMemo(
    () => [
      {
        validator(_: unknown, value: string) {
          if (!value) {
            return Promise.resolve();
          }

          const isDeEdge =
            selectedHotel?.channelManager === ChannelManager.D_EDGE;

          if (isDeEdge) {
            if (!/^[a-z]+(-[a-z]+)*$/.test(value)) {
              return Promise.reject(
                new Error('Format must be kebab-case (e.g. public-rate)')
              );
            }
          } else {
            if (!/^[a-z]+(_[a-z]+)*$/.test(value)) {
              return Promise.reject(
                new Error('Format must be snake_case (e.g. public_rate)')
              );
            }
          }

          return Promise.resolve();
        },
      },
    ],
    [selectedHotel]
  );

  const onChangeHotel = useCallback(
    (id: number) => {
      onHotelSelect(id);
    },
    [onHotelSelect]
  );

  if (!hotels) {
    return null;
  }

  return (
    <div className="basic-info">
      <div
        className={`input-row input-row-hotel-select ${
          mode === 'view' ? 'input-row-no-rules' : ''
        }`}
      >
        <Form.Item name="hotelId" label="Hotel" rules={RULES}>
          {mode === Mode.Edit ? (
            <Select
              placeholder="Select a hotel"
              showSearch
              optionFilterProp="children"
              onChange={onChangeHotel}
              disabled={!newHotel}
            >
              {hotels.map((hotel) => (
                <Option key={hotel.id} value={hotel.id}>
                  {hotel.name}
                </Option>
              ))}
            </Select>
          ) : (
            <div className="view-text">
              {selectedHotel?.name ??
                (values?.hotelId ? `#${values.hotelId}` : null)}
            </div>
          )}
        </Form.Item>
      </div>
      <div
        className={`input-row ${mode === 'view' ? 'input-row-no-rules' : ''}`}
      >
        {!newHotel && (
          <Form.Item name="slug" label="Slug">
            {mode === Mode.Edit || !values ? (
              <Input disabled />
            ) : (
              <div className="view-text">{values.slug}</div>
            )}
          </Form.Item>
        )}
        <Form.Item
          name="aliasForHotel"
          label="Name for hotel"
          rules={RULES}
          extra={
            mode === Mode.Edit || !values ? (
              <span className="sub-text">
                <InfoCircleOutlined className="info-circle" />
                Shown on the Extranet and Bills (E.g. Spa package, Dinner
                package)
              </span>
            ) : (
              <></>
            )
          }
        >
          {mode === Mode.Edit || !values ? (
            <Input />
          ) : (
            <div className="view-text">{values.aliasForHotel}</div>
          )}
        </Form.Item>
        {selectedHotel?.channelManager && !values?.dayPackage && (
          <Form.Item
            name="channelManagerRateCode"
            label="Rate code for hotel"
            rules={channelManagerRateCodeRules}
            extra={
              mode === Mode.Edit || !values ? (
                <span className="sub-text">
                  Code generated by the hotel and used to connect the package to
                  its channel manager.
                </span>
              ) : (
                <></>
              )
            }
          >
            {mode === Mode.Edit || !values ? (
              <Input />
            ) : (
              <div className="view-text">{values?.channelManagerRateCode}</div>
            )}
          </Form.Item>
        )}
        <Form.Item
          name="aliasForUser"
          label="Name for user"
          extra={
            mode === Mode.Edit || !values ? (
              <span className="sub-text">
                <InfoCircleOutlined className="info-circle" />
                Shown on Package page and Search results. If this is the hotel's
                only package, leave empty.
              </span>
            ) : (
              <></>
            )
          }
        >
          {mode === Mode.Edit || !values ? (
            <Input disabled={values?.dayPackage} />
          ) : (
            <div className="view-text">{values.aliasForUser}</div>
          )}
        </Form.Item>
      </div>
      <div
        className={`input-row ${mode === 'view' ? 'input-row-no-rules' : ''}`}
      >
        <Form.Item
          name="dayPackage"
          label="Day Package"
          valuePropName="checked"
        >
          <Checkbox
            disabled={mode !== Mode.Edit}
            onChange={(e: CheckboxChangeEvent) =>
              setFieldValue('aliasForUser', undefined)
            }
          />
        </Form.Item>
      </div>
    </div>
  );
};
